// crewbit-admin/src/components/money/DuplicateInvoiceModal.tsx
//
// §Money.StubActionSweepCompletion Phase 1. Centered confirm modal for the
// duplicate_invoice_as_draft RPC. Non-destructive accent treatment (FilePlus
// icon, accent background) — distinct from the redWash destructive modals.

import { useState } from 'react';
import { FilePlus } from 'lucide-react';
import { palette, spacing, transition } from '../../styles/tokens';
import { duplicateInvoiceAsDraft } from '../../lib/invoiceMutations';

interface Props {
  isOpen: boolean;
  invoiceId: string;
  onClose: () => void;
  onResult: (ok: boolean, message: string) => void;
  onSuccess?: (newInvoiceId: string) => void;
}

export function DuplicateInvoiceModal({ isOpen, invoiceId, onClose, onResult, onSuccess }: Props) {
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const onConfirm = async () => {
    setLoading(true);
    const result = await duplicateInvoiceAsDraft(invoiceId);
    setLoading(false);
    if (result.success) {
      onResult(true, `Draft ${result.data.invoice_number} created.`);
      onClose();
      onSuccess?.(result.data.invoice_id);
    } else {
      onResult(false, result.error.message ?? 'Failed to duplicate invoice.');
    }
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, zIndex: 70,
        background: 'rgba(0,0,0,0.3)',
        backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%', maxWidth: 420, zIndex: 71,
        background: palette.white, borderRadius: 16,
        boxShadow: '0 16px 64px rgba(0,0,0,0.14), 0 4px 16px rgba(0,0,0,0.08)',
        padding: 28,
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 14,
          background: palette.accentWash,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: spacing.md,
        }}>
          <FilePlus size={22} color={palette.accent} strokeWidth={1.8} />
        </div>

        <div style={{ fontSize: 16, fontWeight: 700, color: palette.g800, marginBottom: 6 }}>
          Duplicate this invoice as a new draft?
        </div>
        <div style={{ fontSize: 14, color: palette.g500, lineHeight: 1.5, marginBottom: spacing.md }}>
          You'll be able to edit it before sending.
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: spacing.md }}>
          <button onClick={onClose} disabled={loading} style={{
            padding: '9px 22px', borderRadius: 20,
            border: `1px solid ${palette.g200}`, background: palette.white,
            fontSize: 13.5, fontWeight: 500, color: palette.g600,
            cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            transition: `background ${transition.fast}`,
          }}>Cancel</button>
          <button onClick={onConfirm} disabled={loading} style={{
            padding: '9px 22px', borderRadius: 20, border: 'none',
            background: loading ? palette.g300 : palette.accent, color: palette.white,
            fontSize: 13.5, fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
          }}>{loading ? 'Duplicating…' : 'Duplicate as draft'}</button>
        </div>
      </div>
    </>
  );
}
